import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Typography, TextField, Button, Grid, CircularProgress, Alert,
    Select, MenuItem, InputLabel, FormControl, Dialog, DialogTitle, DialogContent,
    DialogActions, IconButton, Divider, Chip, FormHelperText, useTheme, alpha, Paper
} from '@mui/material';
import { useDropzone } from 'react-dropzone';
import {
    PointOfSale as CategoryIcon,
    LinearScale as TypeIcon,
    ViewInAr as SubtypeIcon,
    Place as LocationIcon,
    Route as NetworkIcon,
    Timeline as SectionIcon,
    SettingsEthernet as ChainageIcon,
    Label as CodeIcon,
    Description as DescriptionIcon,
    CalendarMonth as DateIcon,
    Public as GeometryIcon,
    Close as CloseIcon,
    FileUpload as FileUploadIcon,
    Map as MapIcon
} from '@mui/icons-material';
import {
    organizationsApi, networksApi, sectionsApi, chainageApi,
    classificationApi, assetsApi
} from '../../services/api';
import wellknown from 'wellknown';
import { kml } from '@mapbox/togeojson';
import GeometryEditorModal from './controls/GeometryEditorModal';

const CreateAssetForm = ({ onSubmit, onCancel }) => {
    const theme = useTheme();
    const [isMapModalOpen, setMapModalOpen] = useState(false);

    // Form Data State
    const [formData, setFormData] = useState({
        organization_id: '',
        network_id: '',
        section_id: '',
        chainage_segment_id: '',
        localized_chainage_segment_id: '',
        category_id: '',
        type_id: '',
        subtype_id: '',
        name: '',
        code: '',
        description: '',
        status: 'active',
        install_date: '',
        geometry: '',
    });

    // Dropdown Options State
    const [organizations, setOrganizations] = useState([]);
    const [networks, setNetworks] = useState([]);
    const [sections, setSections] = useState([]);
    const [chainageSegments, setChainageSegments] = useState([]);
    const [localizedSegments, setLocalizedSegments] = useState([]);
    const [categories, setCategories] = useState([]);
    const [types, setTypes] = useState([]);
    const [subtypes, setSubtypes] = useState([]);

    // Geometry Input State
    const [geometryInput, setGeometryInput] = useState('');
    const [geometryError, setGeometryError] = useState(null);
    const [expectedGeometryType, setExpectedGeometryType] = useState(null);
    const [geometrySource, setGeometrySource] = useState(null); // 'file' or 'draw'

    // Loading/Error State
    const [loading, setLoading] = useState({
        orgs: false, networks: false, sections: false, chainage: false, localized: false,
        cats: false, types: false, subtypes: false, submit: false
    });
    const [error, setError] = useState(null);

    // --- Data Fetching Effects ---

    // Fetch initial lists (Orgs, Categories)
    useEffect(() => {
        const fetchInitial = async () => {
            setLoading(prev => ({ ...prev, orgs: true, cats: true }));
            try {
                const [orgRes, catRes] = await Promise.all([
                    organizationsApi.getAll(),
                    classificationApi.getCategories()
                ]);
                setOrganizations(orgRes.data);
                setCategories(catRes.data);
            } catch (err) {
                console.error("Error fetching initial dropdown data:", err);
                setError("Failed to load initial form data.");
            } finally {
                setLoading(prev => ({ ...prev, orgs: false, cats: false }));
            }
        };
        fetchInitial();
    }, []);

    // Fetch Networks when Organization changes
    useEffect(() => {
        if (!formData.organization_id) {
            setNetworks([]);
            setFormData(prev => ({ ...prev, network_id: '', section_id: '', chainage_segment_id: '', localized_chainage_segment_id: '' }));
            return;
        }
        const fetchNetworks = async () => {
            setLoading(prev => ({ ...prev, networks: true }));
            try {
                const response = await networksApi.getAll({ organization_id: formData.organization_id });
                setNetworks(response.data);
            } catch (err) {
                console.error("Error fetching networks:", err);
                setError("Failed to load networks.");
            } finally {
                setLoading(prev => ({ ...prev, networks: false }));
            }
        };
        fetchNetworks();
    }, [formData.organization_id]);

    // Fetch Sections when Network changes
    useEffect(() => {
        if (!formData.network_id) {
            setSections([]);
             setFormData(prev => ({ ...prev, section_id: '', chainage_segment_id: '', localized_chainage_segment_id: '' }));
            return;
        }
        const fetchSections = async () => {
            setLoading(prev => ({ ...prev, sections: true }));
            try {
                const response = await sectionsApi.getAll({ network_id: formData.network_id });
                setSections(response.data);
            } catch (err) {
                console.error("Error fetching sections:", err);
                setError("Failed to load sections.");
            } finally {
                setLoading(prev => ({ ...prev, sections: false }));
            }
        };
        fetchSections();
    }, [formData.network_id]);

    // Fetch Chainage Segments when Section changes
     useEffect(() => {
        if (!formData.section_id) {
            setChainageSegments([]);
             setFormData(prev => ({ ...prev, chainage_segment_id: '', localized_chainage_segment_id: '' }));
            return;
        }
        const fetchChainage = async () => {
            setLoading(prev => ({ ...prev, chainage: true }));
            try {
                const response = await chainageApi.getChainageSegments({ section_id: formData.section_id });
                setChainageSegments(response.data);
            } catch (err) {
                console.error("Error fetching chainage segments:", err);
                setError("Failed to load chainage segments.");
            } finally {
                setLoading(prev => ({ ...prev, chainage: false }));
            }
        };
        fetchChainage();
    }, [formData.section_id]);

    // Fetch Localized Chainage Segments when Chainage Segment changes (Optional step)
     useEffect(() => {
        if (!formData.chainage_segment_id) {
            setLocalizedSegments([]);
             setFormData(prev => ({ ...prev, localized_chainage_segment_id: '' }));
            return;
        }
        const fetchLocalized = async () => {
            setLoading(prev => ({ ...prev, localized: true }));
            try {
                const response = await chainageApi.getLocalizedChainageSegments({ chainage_segment_id: formData.chainage_segment_id });
                setLocalizedSegments(response.data);
            } catch (err) {
                console.error("Error fetching localized segments:", err);
                setError("Failed to load localized segments.");
            } finally {
                setLoading(prev => ({ ...prev, localized: false }));
            }
        };
        fetchLocalized();
    }, [formData.chainage_segment_id]);


    // Fetch Asset Types when Category changes
    useEffect(() => {
        if (!formData.category_id) {
            setTypes([]);
            setFormData(prev => ({ ...prev, type_id: '', subtype_id: '' }));
            return;
        }
        const fetchTypes = async () => {
            setLoading(prev => ({ ...prev, types: true }));
            try {
                const response = await classificationApi.getTypes({ category_id: formData.category_id });
                setTypes(response.data);
            } catch (err) {
                console.error("Error fetching asset types:", err);
                setError("Failed to load asset types.");
            } finally {
                setLoading(prev => ({ ...prev, types: false }));
            }
        };
        fetchTypes();
    }, [formData.category_id]);

    // Fetch Asset Subtypes when Type changes and set expected geometry type
    // Fetch Asset Subtypes when Type changes and set expected geometry type
    useEffect(() => {
        if (!formData.type_id) {
            setSubtypes([]);
            setFormData(prev => ({ ...prev, subtype_id: '' }));
            setExpectedGeometryType(null); // Clear expected type if no type is selected
            return;
        }

        // Find the selected type from the already fetched types list
        const selectedType = types.find(type => type.id === formData.type_id);

        if (selectedType) {
            setExpectedGeometryType(selectedType.geometry_type);
        } else {
            setExpectedGeometryType(null);
        }

        const fetchSubtypes = async () => {
            setLoading(prev => ({ ...prev, subtypes: true }));
            try {
                const response = await classificationApi.getSubtypes({ type_id: formData.type_id });
                setSubtypes(response.data);
            } catch (err) {
                console.error("Error fetching asset subtypes:", err);
                setError("Failed to load asset subtypes.");
            } finally {
                setLoading(prev => ({ ...prev, subtypes: false }));
            }
        };
        fetchSubtypes();

    }, [formData.type_id, types]); // Depend on formData.type_id and types state


    // --- Handlers ---
    const handleChange = (event) => {
        const { name, value } = event.target;
        setFormData(prev => ({
            ...prev,
            [name]: value,
        }));
        setError(null); // Clear error on change
    };


    const onDrop = useCallback((acceptedFiles) => {
        if (acceptedFiles.length === 0) {
            setGeometryError("No file selected or file type not accepted.");
            setFormData(prev => ({ ...prev, geometry: '' }));
            setGeometryInput('');
            return;
        }

        const file = acceptedFiles[0];
        const reader = new FileReader();

        reader.onabort = () => {
            setGeometryError('File reading was aborted');
            setFormData(prev => ({ ...prev, geometry: '' }));
            setGeometryInput('');
        };
        reader.onerror = () => {
            setGeometryError('File reading has failed');
            setFormData(prev => ({ ...prev, geometry: '' }));
            setGeometryInput('');
        };
        reader.onload = () => {
            const fileContent = reader.result;
            setGeometryInput(fileContent); // Store raw input for display if needed
            processGeometryInput(fileContent, 'file');
        };

        reader.readAsText(file);
    }, [expectedGeometryType, formData.type_id]); // Depend on expectedGeometryType and type_id

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: {
            'application/vnd.google-earth.kml+xml': ['.kml'],
            'application/geo+json': ['.geojson', '.json']
        },
        multiple: false
    });

    const processGeometryInput = (inputString) => {
        setGeometryError(null);
        let wktGeometry = '';
        let geometryType = null;
        let parsedSuccessfully = false;

        // Attempt to parse as GeoJSON first
        try {
            const geojson = JSON.parse(inputString);
            if (geojson && geojson.type && geojson.coordinates) {
                // Handle FeatureCollection or Feature
                let featureGeometry = geojson;
                if (geojson.type === 'FeatureCollection' && geojson.features && geojson.features.length > 0) {
                    featureGeometry = geojson.features[0].geometry; // Take the first feature's geometry
                } else if (geojson.type === 'Feature' && geojson.geometry) {
                    featureGeometry = geojson.geometry;
                }

                if (featureGeometry && featureGeometry.type && featureGeometry.coordinates) {
                    // Strip Z-coordinate before stringifying
                    const geom2d = JSON.parse(JSON.stringify(featureGeometry)); // Deep copy
                    if (geom2d.coordinates) {
                        // Recursively remove the Z dimension
                        const stripZ = (coords) => {
                            if (Array.isArray(coords) && coords.length > 0) {
                                if (typeof coords[0] === 'number' && coords.length > 2) {
                                    return coords.slice(0, 2);
                                }
                                return coords.map(stripZ);
                            }
                            return coords;
                        };
                        geom2d.coordinates = stripZ(geom2d.coordinates);
                    }
                    wktGeometry = wellknown.stringify(geom2d);
                    geometryType = featureGeometry.type.toLowerCase();
                    parsedSuccessfully = true;
                } else {
                    // It parsed as JSON but doesn't look like valid GeoJSON structure
                    throw new Error("Invalid GeoJSON structure.");
                }
            }
        } catch (jsonError) {
            // JSON parsing failed, ignore and proceed to KML parsing
            console.log("Input is not valid JSON, attempting KML parse.");
        }

        // If GeoJSON parsing failed, attempt to parse as KML
        if (!parsedSuccessfully) {
            try {
                const parser = new DOMParser();
                const kmlDoc = parser.parseFromString(inputString, "text/xml");
                // Check for parser errors (important for malformed XML)
                const parserError = kmlDoc.getElementsByTagName("parsererror");
                if (parserError.length > 0) {
                    throw new Error("Invalid KML format (XML parsing error).");
                }

                const geojsonFromKml = kml(kmlDoc);

                if (geojsonFromKml && geojsonFromKml.features && geojsonFromKml.features.length > 0) {
                    const featureGeometry = geojsonFromKml.features[0].geometry; // Take the first feature's geometry
                    if (featureGeometry && featureGeometry.type && featureGeometry.coordinates) {
                        // Strip Z-coordinate before stringifying
                        const geom2d = JSON.parse(JSON.stringify(featureGeometry)); // Deep copy
                        if (geom2d.coordinates) {
                            // Recursively remove the Z dimension
                            const stripZ = (coords) => {
                                if (Array.isArray(coords) && coords.length > 0) {
                                    if (typeof coords[0] === 'number' && coords.length > 2) {
                                        return coords.slice(0, 2);
                                    }
                                    return coords.map(stripZ);
                                }
                                return coords;
                            };
                            geom2d.coordinates = stripZ(geom2d.coordinates);
                        }
                        wktGeometry = wellknown.stringify(geom2d);
                        geometryType = featureGeometry.type.toLowerCase();
                        parsedSuccessfully = true;
                    } else {
                        throw new Error("No valid geometry found within the KML structure.");
                    }
                } else {
                    throw new Error("Could not extract features from KML.");
                }
            } catch (kmlError) {
                console.error("KML processing error:", kmlError);
                setGeometryError(`Error processing KML: ${kmlError.message}`);
                setFormData(prev => ({ ...prev, geometry: '' })); // Clear geometry on error
                return; // Stop processing if KML fails
            }
        }

        // If neither GeoJSON nor KML parsing succeeded
        if (!parsedSuccessfully) {
             setGeometryError("Could not parse input as valid GeoJSON or KML.");
             setFormData(prev => ({ ...prev, geometry: '' }));
             return;
        }

        // Validate geometry type if an asset type is selected
        if (expectedGeometryType && geometryType !== expectedGeometryType.toLowerCase()) {
             setGeometryError(`Geometry type mismatch. Expected: ${expectedGeometryType}, Received: ${geometryType}`);
             setFormData(prev => ({ ...prev, geometry: '' })); // Clear geometry if type is wrong
        } else {
             setFormData(prev => ({ ...prev, geometry: wktGeometry }));
             setGeometrySource('file');
        }
    };


    const handleSubmit = async (event) => {
        event.preventDefault();
        setLoading(prev => ({ ...prev, submit: true }));
        setError(null);
        setGeometryError(null); // Clear geometry error on submit attempt
        try {
            // Basic validation
            if (!formData.name || !formData.code || !formData.subtype_id || !formData.section_id) {
                 throw new Error("Required fields (Name, Code, Subtype, Section) are missing.");
            }

            // Validate geometry is present and processed
            if (!formData.geometry) {
                 throw new Error("Geometry is required. Please upload a valid KML or GeoJSON file.");
            }

            // Prepare data for API (remove empty optional fields if needed)
            const dataToSend = { ...formData };
            if (!dataToSend.chainage_segment_id) delete dataToSend.chainage_segment_id;
            if (!dataToSend.localized_chainage_segment_id) delete dataToSend.localized_chainage_segment_id;
            if (!dataToSend.install_date) delete dataToSend.install_date;
            if (!dataToSend.description) delete dataToSend.description;
            // geometry is required now, so no need to delete if empty
            // delete dataToSend.geometry;
            // Remove category_id and type_id as they are not directly stored on the asset
            delete dataToSend.category_id;
            delete dataToSend.type_id;


            await assetsApi.create(dataToSend);
            onSubmit(dataToSend); // Pass data back to parent on success
        } catch (err) {
            console.error("Error creating asset:", err);
            const errorMessage = err.response?.data?.error || err.message || "Failed to create asset.";
            setError(errorMessage);
            // Pass error information to parent component
            onSubmit(formData, { error: errorMessage });
        } finally {
            setLoading(prev => ({ ...prev, submit: false }));
        }
    };

    // Helper to render Select options
    const renderOptions = (items) => items.map(item => (
        <MenuItem key={item.id} value={item.id}>
            {item.name} {item.code ? <Chip size="small" label={item.code} sx={{ ml: 1 }} /> : ''}
        </MenuItem>
    ));


    const handleSaveGeometry = (geoJson) => {
        if (geoJson) {
            // Since we know this is a GeoJSON object, we can process it directly
            // without going through the KML parsing logic.
            try {
                setGeometryError(null);
                const featureGeometry = geoJson.geometry || geoJson;

                // Strip Z-coordinate before stringifying
                const geom2d = JSON.parse(JSON.stringify(featureGeometry)); // Deep copy
                if (geom2d.coordinates) {
                    const stripZ = (coords) => {
                        if (Array.isArray(coords) && coords.length > 0) {
                            if (typeof coords[0] === 'number' && coords.length > 2) {
                                return coords.slice(0, 2);
                            }
                            return coords.map(stripZ);
                        }
                        return coords;
                    };
                    geom2d.coordinates = stripZ(geom2d.coordinates);
                }
                const wktGeometry = wellknown.stringify(geom2d);
                const geometryType = featureGeometry.type.toLowerCase();

                if (expectedGeometryType && geometryType !== expectedGeometryType.toLowerCase()) {
                    setGeometryError(`Geometry type mismatch. Expected: ${expectedGeometryType}, Received: ${geometryType}`);
                    setFormData(prev => ({ ...prev, geometry: '' }));
                } else {
                    setFormData(prev => ({ ...prev, geometry: wktGeometry }));
                    setGeometrySource('draw');
                }
            } catch (err) {
                setGeometryError("Failed to process the drawn geometry.");
            }
        }
        setMapModalOpen(false);
    };

    return (
        <React.Fragment>
            <DialogTitle sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                borderBottom: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
                py: 1.5,
                px: 2.5,
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <DescriptionIcon color="primary" />
                    <Typography variant="h6" component="h2" sx={{ fontWeight: 600 }}>
                        Create New Asset
                    </Typography>
                </Box>
                <IconButton onClick={onCancel} size="small">
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers sx={{ p: 3 }}>
                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                <Grid container spacing={3}>
                    {/* Classification Section */}
                    <Grid item xs={12}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: 'text.primary' }}>Classification</Typography>
                        <Grid container spacing={2}>
                            <Grid item xs={4}>
                                <FormControl fullWidth required disabled={loading.cats} error={!formData.category_id && loading.submit}>
                                    <InputLabel>Category</InputLabel>
                                    <Select 
                                        name="category_id" 
                                        value={formData.category_id} 
                                        label="Category" 
                                        onChange={handleChange}
                                        MenuProps={{
                                            PaperProps: {
                                                style: {
                                                    maxHeight: 300,
                                                }
                                            }
                                        }}
                                        renderValue={(selected) => {
                                            const selectedItem = categories.find(cat => cat.id === selected);
                                            return selectedItem ? (
                                                <Box sx={{ display: 'flex', alignItems: 'center', overflow: 'hidden' }}>
                                                    <Typography noWrap sx={{ maxWidth: '100%' }}>
                                                        {selectedItem.name}
                                                    </Typography>
                                                </Box>
                                            ) : '';
                                        }}
                                    >
                                        {renderOptions(categories)}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={4}>
                                <FormControl fullWidth required disabled={!formData.category_id || loading.types} error={!formData.type_id && loading.submit}>
                                    <InputLabel>Type</InputLabel>
                                    <Select 
                                        name="type_id" 
                                        value={formData.type_id} 
                                        label="Type" 
                                        onChange={handleChange}
                                        MenuProps={{
                                            PaperProps: {
                                                style: {
                                                    maxHeight: 300,
                                                }
                                            }
                                        }}
                                        renderValue={(selected) => {
                                            const selectedItem = types.find(type => type.id === selected);
                                            return selectedItem ? (
                                                <Box sx={{ display: 'flex', alignItems: 'center', overflow: 'hidden' }}>
                                                    <Typography noWrap sx={{ maxWidth: '100%' }}>
                                                        {selectedItem.name}
                                                    </Typography>
                                                </Box>
                                            ) : '';
                                        }}
                                    >
                                        {renderOptions(types)}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={4}>
                                <FormControl fullWidth required disabled={!formData.type_id || loading.subtypes} error={!formData.subtype_id && loading.submit}>
                                    <InputLabel>Subtype</InputLabel>
                                    <Select 
                                        name="subtype_id" 
                                        value={formData.subtype_id} 
                                        label="Subtype" 
                                        onChange={handleChange}
                                        MenuProps={{
                                            PaperProps: {
                                                style: {
                                                    maxHeight: 300,
                                                }
                                            }
                                        }}
                                        renderValue={(selected) => {
                                            const selectedItem = subtypes.find(subtype => subtype.id === selected);
                                            return selectedItem ? (
                                                <Box sx={{ display: 'flex', alignItems: 'center', overflow: 'hidden' }}>
                                                    <Typography noWrap sx={{ maxWidth: '100%' }}>
                                                        {selectedItem.name}
                                                    </Typography>
                                                </Box>
                                            ) : '';
                                        }}
                                    >
                                        {renderOptions(subtypes)}
                                    </Select>
                                </FormControl>
                            </Grid>
                        </Grid>
                    </Grid>

                    <Grid item xs={12}><Divider /></Grid>

                    {/* Location Section */}
                    <Grid item xs={12}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: 'text.primary' }}>Location</Typography>
                        <Grid container spacing={2}>
                            <Grid item xs={3}>
                                <FormControl fullWidth required error={!formData.organization_id && loading.submit}>
                                    <InputLabel>Organization</InputLabel>
                                    <Select 
                                        name="organization_id" 
                                        value={formData.organization_id} 
                                        label="Organization" 
                                        onChange={handleChange} 
                                        disabled={loading.orgs}
                                        MenuProps={{
                                            PaperProps: {
                                                style: {
                                                    maxHeight: 300,
                                                }
                                            }
                                        }}
                                        renderValue={(selected) => {
                                            const selectedItem = organizations.find(org => org.id === selected);
                                            return selectedItem ? (
                                                <Box sx={{ display: 'flex', alignItems: 'center', overflow: 'hidden' }}>
                                                    <Typography noWrap sx={{ maxWidth: '100%' }}>
                                                        {selectedItem.name}
                                                    </Typography>
                                                </Box>
                                            ) : '';
                                        }}
                                    >
                                        {renderOptions(organizations)}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={3}>
                                <FormControl fullWidth required disabled={!formData.organization_id || loading.networks} error={!formData.network_id && loading.submit}>
                                    <InputLabel>Network</InputLabel>
                                    <Select 
                                        name="network_id" 
                                        value={formData.network_id} 
                                        label="Network" 
                                        onChange={handleChange}
                                        MenuProps={{
                                            PaperProps: {
                                                style: {
                                                    maxHeight: 300,
                                                }
                                            }
                                        }}
                                        renderValue={(selected) => {
                                            const selectedItem = networks.find(net => net.id === selected);
                                            return selectedItem ? (
                                                <Box sx={{ display: 'flex', alignItems: 'center', overflow: 'hidden' }}>
                                                    <Typography noWrap sx={{ maxWidth: '100%' }}>
                                                        {selectedItem.name}
                                                    </Typography>
                                                </Box>
                                            ) : '';
                                        }}
                                    >
                                        {renderOptions(networks)}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={3}>
                                <FormControl fullWidth required disabled={!formData.network_id || loading.sections} error={!formData.section_id && loading.submit}>
                                    <InputLabel>Section</InputLabel>
                                    <Select 
                                        name="section_id" 
                                        value={formData.section_id} 
                                        label="Section" 
                                        onChange={handleChange}
                                        MenuProps={{
                                            PaperProps: {
                                                style: {
                                                    maxHeight: 300,
                                                }
                                            }
                                        }}
                                        renderValue={(selected) => {
                                            const selectedItem = sections.find(sec => sec.id === selected);
                                            return selectedItem ? (
                                                <Box sx={{ display: 'flex', alignItems: 'center', overflow: 'hidden' }}>
                                                    <Typography noWrap sx={{ maxWidth: '100%' }}>
                                                        {selectedItem.name}
                                                    </Typography>
                                                </Box>
                                            ) : '';
                                        }}
                                    >
                                        {renderOptions(sections)}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={3}>
                                <FormControl fullWidth disabled={!formData.section_id || loading.chainage}>
                                    <InputLabel>Chainage Segment</InputLabel>
                                    <Select 
                                        name="chainage_segment_id" 
                                        value={formData.chainage_segment_id} 
                                        label="Chainage Segment" 
                                        onChange={handleChange}
                                        MenuProps={{
                                            PaperProps: {
                                                style: {
                                                    maxHeight: 300,
                                                }
                                            }
                                        }}
                                        renderValue={(selected) => {
                                            const selectedItem = chainageSegments.find(seg => seg.id === selected);
                                            return selectedItem ? (
                                                <Box sx={{ display: 'flex', alignItems: 'center', overflow: 'hidden' }}>
                                                    <Typography noWrap sx={{ maxWidth: '100%' }}>
                                                        {selectedItem.name}
                                                    </Typography>
                                                </Box>
                                            ) : '';
                                        }}
                                    >
                                        {renderOptions(chainageSegments)}
                                    </Select>
                                </FormControl>
                            </Grid>
                        </Grid>
                    </Grid>

                    <Grid item xs={12}><Divider /></Grid>

                    {/* Details Section */}
                    <Grid item xs={12}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: 'text.primary' }}>Details</Typography>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <TextField fullWidth required name="name" label="Asset Name" value={formData.name} onChange={handleChange} error={!formData.name && loading.submit} />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField fullWidth required name="code" label="Asset Code" value={formData.code} onChange={handleChange} error={!formData.code && loading.submit} />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField fullWidth name="description" label="Description" value={formData.description} onChange={handleChange} multiline rows={2} />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <FormControl fullWidth>
                                    <InputLabel>Status</InputLabel>
                                    <Select name="status" value={formData.status} label="Status" onChange={handleChange}>
                                        <MenuItem value="active">Active</MenuItem>
                                        <MenuItem value="inactive">Inactive</MenuItem>
                                        <MenuItem value="maintenance">Maintenance</MenuItem>
                                        <MenuItem value="decommissioned">Decommissioned</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField fullWidth name="install_date" label="Install Date" type="date" value={formData.install_date} onChange={handleChange} InputLabelProps={{ shrink: true }} />
                            </Grid>
                        </Grid>
                    </Grid>

                    <Grid item xs={12}><Divider /></Grid>

                    {/* Geometry Section */}
                    <Grid item xs={12}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: 'text.primary' }}>Geometry</Typography>
                        <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12} sm={8}>
                                <FormControl fullWidth error={!!geometryError}>
                                    <Paper variant="outlined" {...getRootProps()} sx={{ p: 2, textAlign: 'center', cursor: 'pointer', borderColor: isDragActive ? 'primary.main' : 'divider', borderStyle: 'dashed', backgroundColor: isDragActive ? 'action.hover' : 'transparent' }}>
                                        <input {...getInputProps()} />
                                        <FileUploadIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                                        <Typography variant="body2" component="span">Drag & drop file, or click to select</Typography>
                                        <Typography variant="caption" display="block" sx={{ mt: 1, color: 'text.secondary' }}>
                                            Accepted formats: .kml, .geojson, .json
                                        </Typography>
                                    </Paper>
                                    {geometryError && <FormHelperText>{geometryError}</FormHelperText>}
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={1} sx={{ textAlign: 'center' }}>
                                <Typography color="text.secondary">OR</Typography>
                            </Grid>
                            <Grid item xs={12} sm={3}>
                                <Button fullWidth variant="outlined" startIcon={<MapIcon />} onClick={() => setMapModalOpen(true)} disabled={!expectedGeometryType}>
                                    Draw on Map
                                </Button>
                            </Grid>
                            <Grid item xs={12}>
                                {!expectedGeometryType && <FormHelperText>Please select an asset type to enable geometry input.</FormHelperText>}
                                {geometrySource && (
                                    <Alert severity="success" sx={{ mt: 1 }}>
                                        Geometry captured successfully from {geometrySource === 'file' ? 'uploaded file' : 'map drawing'}.
                                    </Alert>
                                )}
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions sx={{
                px: 3,
                py: 2,
                borderTop: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
            }}>
                <Button onClick={onCancel} color="inherit">Cancel</Button>
                <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={loading.submit || !formData.geometry}
                >
                    {loading.submit ? <CircularProgress size={24} /> : 'Create Asset'}
                </Button>
            </DialogActions>
            <GeometryEditorModal
                open={isMapModalOpen}
                onClose={() => setMapModalOpen(false)}
                onSave={handleSaveGeometry}
                expectedGeometryType={expectedGeometryType}
            />
        </React.Fragment>
    );
};

export default CreateAssetForm;